// внимание! установка языка должна быть ДО всех импортов,
// т.к. импортируемые модули могут использовать переводы
import './service/Initi18n'; // tslint:disable-next-line ordered-imports
import * as Cookie from 'js-cookie';
import * as React from 'react';
import { ReactElement, ReactNode } from 'react';
import * as ReactDOM from 'react-dom';

import { QueryExecutor } from './api/QueryExecutor';
import { RecommendationsMutationExecutor } from './api/recommendations/RecommendationsMutationExecutor';
import { WalletDataProvider } from './api/wallet/WalletDataProvider';
import { WalletMutationExecutor } from './api/wallet/WalletMutationExecutor';
import { Html5CreativePreview } from './canvas/Html5CreativePreview/Html5CreativePreview';
import { ImageCreativePreview } from './canvas/ImageCreativePreview/ImageCreativePreview';
import { VideoCreativePreview } from './canvas/VideoCreativePreview/VideoCreativePreview';
import { CanvasAppWrapper } from './canvas/wrappers/CanvasAppWrapper/CanvasAppWrapper';
import { Html5AppWrapper } from './canvas/wrappers/Html5AppWrapper/Html5AppWrapper';
import { BalanceCart } from './components/BalanceCart/BalanceCart';
import { BannersPriceEditor } from './components/BannersPriceEditor/BannersPriceEditor';
import { CampaignTypesList } from './components/CampaignTypesList/CampaignTypesList';
import { DisplayMapProvider } from './components/DisplayMapProvider/DisplayMapProvider';
import { ExperimentsProvider } from './components/ExperimentsProvider/ExperimentsProvider';
import { FeedbackTooltip } from './components/FeedbackTooltip/FeedbackTooltip';
import { FirstCampaignTizer } from './components/FirstCampaignTizer/FirstCampaignTizer';
import './components/FixedDataTable/FixedDataTable';
import { FreelancerInfoPanel } from './components/FreelancerInfoPanel/FreelancerInfoPanel';
import './components/Lego/Lego';
import { MobileAppsCreateSingle } from './components/MobileAppsCreateSingle/MobileAppsCreateSingle';
import { OutdoorMap } from './components/OutdoorMap/OutdoorMap';
import { Root } from './components/Root/Root';
import { WalletSwitchOffLimitEditPopupWrapper } from './components/WalletSwitchOffLimitEditPopupWrapper/WalletSwitchOffLimitEditPopupWrapper';
import { getDNAApolloClient } from './declarations/apollo';
import { FiltersForHistotyOfChanges } from './declarations/log-filters/log-filters';
import './index.css';
import { getCSRFToken } from './service/ApiHelpers/ApiHelpers';
import { initErrorsReporting } from './service/ErrorsReporting/ErrorsReporting';
import { serializeFilterToString } from './service/FilterHelpers/FilterHelpers';
import { HeaderHelpers } from './service/HeaderHelpers/HeaderHelpers';

import { createTextAdGroup, getGroupsMutationExecutor } from './service/mutationHelpers/mutationHelpers';
import { PcodeLoader, PcodeRenderer } from './service/Pcode/Pcode';
import { safeCreateElement } from './service/ReactHelper';
import { RUM } from './service/RUM/RUM';
import { CampaignType } from './typings/domain';
import { CurrencyCode, executeRecommendationMutation } from './typings/graphql-grid';
import { GridScreenUrlSearchQueryParams } from './typings/grid';
import { RecommendationKey } from './typings/recommendations';
import './vars.css';

// функция создания "безопасного" элемента, см. DIRECT-83385
export type createSafeElementType = <P extends {}>(
    componentClass: React.ComponentClass<P>,
    props: P,
    children: ReactNode[]
) => ReactElement<P>;

const isDevelopmentEnv = process.env.NODE_ENV === 'development';
const isLocalDevelopment = process.env.REACT_APP_TARGET !== 'direct';
const win = window as Window;
win.PcodeLoader = PcodeLoader;
win.PcodeRenderer = PcodeRenderer;

RUM.init();
RUM.sendTTIData();
initErrorsReporting(isDevelopmentEnv);

if (isLocalDevelopment) {
    const params = new URLSearchParams(window.location.search);
    const cookieLogin = Cookie.get('yandex_login');
    const isPublicDomain = !cookieLogin && window.location.href.includes('freelancers');

    let login = params.get(GridScreenUrlSearchQueryParams.ulogin) || cookieLogin || (isPublicDomain ? 'yuber' : null);

    if (login) {
        const escapeHtml = require('escape-html');

        login = escapeHtml(login);
    }

    if (login === null) {
        throw new Error('login must be defined');
    }

    Root.initStore({
        login,
        isPublicDomain,
        officeContacts: {
            support: {
                landline: [['+7 (495) 739-37-77', 0, '']],
                regional: [['8 800 234-24-80', 0, '']]
            },
            email: 'adv@yandex-team.ru'
        },
        // передаем для локальной разработки, в проде availableCampaignsTypes для начальной инициализации берется тут
        // https://a.yandex-team.ru/arc/trunk/arcadia/direct/perl/data3/desktop.blocks/p-grid/p-grid.bemtree.js?rev=6046238#L100
        availableCampaignsTypes: [
            CampaignType.TEXT,
            CampaignType.PERFORMANCE,
            CampaignType.MOBILE_CONTENT,
            CampaignType.MCBANNER,
            CampaignType.DYNAMIC,
            CampaignType.CPM_DEALS,
            CampaignType.CPM_BANNER,
            CampaignType.CPM_YNDX_FRONTPAGE
        ],
        welcomeParams: {
            country: '208',
            currency: CurrencyCode.RUB
        },
        cspNonce: 'nonce'
    });

    ReactDOM.render(
        <Root
            login={login}
            enableRecommendations={true}
            gridAllowed={true}
            rmpEnabled={true}
            isWebvisorEnabled={false}
            dealsEnabled={true}
            domainsReportEnabled={true}
            isHeaderAvailable={true}
            dealsReadonly={false}
            enableSidebar={true}
            minusPhraseLibraryEnabled={true}
            isCampaignUpdateAllowed={true}
            videoEditorEnabled={true}
        />,
        document.getElementById('root')
    );

    // @ts-ignore
    win.dna = { components: { Root } };
} else {
    win.dna = {
        reactDOMRender: ReactDOM.render.bind(ReactDOM),
        reactDOMUnmount: ReactDOM.unmountComponentAtNode.bind(ReactDOM),
        reactCreateElement: safeCreateElement,
        utils: {
            getQueryExecutor: (login: string): QueryExecutor => {
                const dnaClient = getDNAApolloClient(login, getCSRFToken);
                return new QueryExecutor(dnaClient);
            },
            getGroupsMutationExecutor: getGroupsMutationExecutor,
            serializeLogFilterToString: serializeFilterToString,
            filtersForHistotyOfChanges: FiltersForHistotyOfChanges,
            createTextAdGroup: createTextAdGroup, // todo @mrronuar удалить после закрытия тикета DIRECT-107659
            executeRecommendation: (
                login: string,
                keys: RecommendationKey[]
            ): Promise<executeRecommendationMutation> => {
                const dnaClient = getDNAApolloClient(login, getCSRFToken);
                const recommendationExecutor = new RecommendationsMutationExecutor(dnaClient);

                return recommendationExecutor.executeRecommendation(keys);
            },
            getSwitchOffLimitDataProvider: (
                login: string
            ): { dataProvider: WalletDataProvider; mutationExecutor: WalletMutationExecutor } => {
                const dnaClient = getDNAApolloClient(login, getCSRFToken);

                const walletDataProvider = new WalletDataProvider(dnaClient);
                const walletMutationExecutor = new WalletMutationExecutor(dnaClient);

                return {
                    dataProvider: walletDataProvider,
                    mutationExecutor: walletMutationExecutor
                };
            }
        },
        components: {
            Root: Root,
            BalanceCart: BalanceCart,
            FeedbackTooltip: FeedbackTooltip,
            FreelancerInfoPanel: FreelancerInfoPanel,
            CampaignTypesList: CampaignTypesList,
            ExperimentsControl: ExperimentsProvider, // TODO: Для обратной совместимости, удалить когдай выкатится DIRECT-79148
            ExperimentsProvider: ExperimentsProvider,
            OutdoorMap: OutdoorMap,
            // Generator: Generator,
            DisplayMapProvider: DisplayMapProvider,
            FirstCampaignTizer: FirstCampaignTizer,
            MobileAppsCreateSingle: MobileAppsCreateSingle,
            WalletSwitchOffLimitEditPopupWrapper: WalletSwitchOffLimitEditPopupWrapper,
            BannersPriceEditor: BannersPriceEditor,
            Html5App: Html5AppWrapper,
            CanvasApp: CanvasAppWrapper,
            VideoCreativePreview: VideoCreativePreview,
            ImageCreativePreview: ImageCreativePreview,
            Html5CreativePreview: Html5CreativePreview
        },
        helpers: {
            HeaderHelpers: HeaderHelpers
        }
    };
}
